'use client';
/**
 * oc-tag (2026-11-01 · Node 「C로 가자」) — 화면 오른쪽 가장자리에 붙는 작은 노란 태그 「정보방」 → 부정공 부동산 정보공유방(카카오 오픈채팅).
 *
 *  · 자리: 오른쪽 끝(본문을 거의 가리지 않음) · 아래는 하단 탭바/상세 하단 바(.mbar)/유료 도크 «위».
 *  · 등장: oc-always (2026-11-02 · Node 「정보방 메뉴 항시 떠있게」) — 페이지가 뜨자마자 «항상» 보인다(지연·스크롤 조건 없음 · 눌러도 안 사라짐).
 *    서버 HTML 에는 없다(마운트 직후 표시 · fixed 라 CLS 0).
 *  · 잠깐 비킴: 입력칸에 글자를 치는 동안만(화면 키보드가 올라와 리드폼 제출 버튼을 가리지 않게) — 입력이 끝나면 바로 돌아온다.
 *  · 종전 «누르면 30일 숨김» 저장값(kd_oc_hide_until)은 읽지 않고 지운다.
 *  · 계측: talk-banner 슬롯 'fab'(user_events banner_view/banner_click · 슬롯 비교 쿼리 그대로).
 *  · 링크: 새 창 · rel nofollow noopener(검색 평가에 영향 없음).
 */
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { KAKAO_TALK_URL, trackTalkClick, trackTalkView } from '@/lib/talk-banner';
import { openchatTagAllowed, siteSlugOf, OC_HIDE_KEY } from './openchat-route';
import './openchat-tag.css';

export default function OpenchatTag() {
  const pathname = usePathname() ?? '';
  const allowed = openchatTagAllowed(pathname);
  const [shown, setShown] = useState(false);
  const [tucked, setTucked] = useState(false);
  const slug = siteSlugOf(pathname);
  const props = slug ? { site_slug: slug, variant: 'edge_tag' } : { variant: 'edge_tag' };

  // 노출 = 태그가 실제로 화면에 나온 때(fixed · 화면 안). useTalkView 는 마운트 때 한 번만 ref 를 보므로 늦게 뜨는 이 태그엔 직접 센다.
  useEffect(() => {
    if (shown && allowed) trackTalkView('fab', props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, allowed, slug]);

  useEffect(() => {
    if (!allowed) return;
    try { localStorage.removeItem(OC_HIDE_KEY); } catch { /* 저장소 막힘 = 무시 */ }
    setShown(true);
    const onFocus = () => {
      const a = document.activeElement as HTMLElement | null;
      setTucked(!!a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName));
    };
    const onBlur = () => window.setTimeout(onFocus, 0);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', onBlur);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', onBlur);
    };
  }, [allowed, pathname]);

  if (!allowed || !shown) return null;
  return (
    <a
      className={`kd-oc${tucked ? ' is-tucked' : ''}`}
      href={KAKAO_TALK_URL}
      target="_blank"
      rel="noopener nofollow"
      aria-label="부정공 부동산 정보공유방(카카오톡 오픈채팅) 새 창으로 열기"
      title="부정공 부동산 정보공유방"
      data-cta="openchat_tag"
      onClick={() => {
        trackTalkClick('fab', props);
      }}
    >
      <svg className="kd-oc__ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path className="kd-oc__bub" d="M12 3.5c-5 0-9 3.1-9 7 0 2.5 1.7 4.7 4.2 5.9l-.9 3.4c-.1.3.3.6.6.4l4-2.6c.4 0 .7.1 1.1.1 5 0 9-3.1 9-7s-4-7.2-9-7.2z" />
        <path className="kd-oc__ln" d="M7.6 9.6h8.8v1.3H7.6zm0 2.5h6v1.3h-6z" />
      </svg>
      <span className="kd-oc__t">정보방</span>
    </a>
  );
}
